import React, { useMemo } from 'react';
import { isUndefined, min } from 'lodash/fp';

import { PieChart, PieColors, PieChartAnalyticsData } from '../../../shared/analytics/PieChart';
import { Icon } from '../../../shared/icon/Icon';
import styles from './ChatBot.module.scss';
import { InformationTooltip } from '../../../shared/tooltip';

interface ChatBotPieChartWrapperProps {
    data: PieChartAnalyticsData[];
    dimensions?: {
        height: number;
        width: number;
    }
    title: string;
    description: string;
    id: string
    total?: number;
    slicePercentage?: number | null;
    testId?: string;
    icon?: React.FC;
    colors?: keyof PieColors;
    onDblClick?: (key: string) => void;
    onHover?: (data: PieChartAnalyticsData | null, id: string) => void;
}

export const ChatBotPieChartWrapper: React.FC<ChatBotPieChartWrapperProps> = ({ title, data, testId, dimensions, icon, colors, description, id, onDblClick, total, slicePercentage, onHover }) => {
    const diameter = useMemo(() => {
        if (dimensions) {
            const svgWidth = dimensions.width - 20;
            const svgHeight = dimensions.height - 55;
            return min([svgWidth, svgHeight])!;
        }
        return 0;
    }, [dimensions]);

    const filteredData = useMemo(() => data.filter(({ value }) => value), [data]);

    return (
        <div className={styles.pieChartWrapper} data-testid={`pie-chart-wrapper-${testId}`}>
            <div className={styles.pieChartHeader}>
                <div className={styles.pieChartTitle} data-testid={`pie-chart-title-${testId}`}>{title}</div>
                {description && <div className={styles.pieChartDescription}><InformationTooltip content={description} /></div>}
            </div>
            <PieChart data={filteredData} id={id} testId={testId} diameter={diameter} colors={colors} onDblClick={onDblClick} onHover={onHover} />
            <div
                className={styles.pieChartIcon}
                style={{ height: `${diameter / 3}px`, width: `${diameter / 3}px` }}
                data-testid={`pie-chart-icon-${testId}`}
            >
                {!isUndefined(total) && !slicePercentage && <div>Total: {total}</div>}
                {icon && !slicePercentage && <Icon icon={icon} fontSize={diameter / 5} />}
                {slicePercentage && <div className={styles.slicePercentage}>{slicePercentage}%</div>}
            </div>
        </div>
    );
};
